import { useCallback, useEffect, useState } from "react";
import clinicAPI from "../../../services/clinicApi";
import s from "../clinic.module.css";
import { Button, Field, Input, useToast } from "../../ui";

const STATUS_LABELS = {
  pending: "Не привязан",
  sent: "Приглашение отправлено",
  registered: "Зарегистрирован",
};

function emptyForm() {
  return {
    contact_name: "",
    contact_phone: "",
    contact_email: "",
    relation: "",
  };
}

export default function AdminGuardiansPanel({ patientId, onChanged }) {
  const toast = useToast();
  const [guardians, setGuardians] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    if (!patientId) return;
    setLoading(true);
    try {
      const data = await clinicAPI.getAthleteGuardians(patientId);
      setGuardians(data.guardians || []);
    } catch (e) {
      toast.error(e.response?.data?.error || "Не удалось загрузить родителей");
      setGuardians([]);
    } finally {
      setLoading(false);
    }
  }, [patientId, toast]);

  useEffect(() => {
    load();
  }, [load]);

  const addGuardian = async (e) => {
    e.preventDefault();
    if (!form.contact_name.trim()) {
      toast.error("Укажите ФИО родителя");
      return;
    }
    if (!form.contact_phone.trim() && !form.contact_email.trim()) {
      toast.error("Укажите телефон или email");
      return;
    }
    setSaving(true);
    try {
      await clinicAPI.addAthleteGuardian(patientId, {
        contact_name: form.contact_name.trim(),
        contact_phone: form.contact_phone.trim() || undefined,
        contact_email: form.contact_email.trim() || undefined,
        relation: form.relation.trim() || undefined,
        is_primary: guardians.length === 0,
      });
      toast.success("Родитель добавлен");
      setForm(emptyForm());
      setShowForm(false);
      load();
      onChanged?.();
    } catch (err) {
      toast.error(err.response?.data?.error || "Ошибка");
    } finally {
      setSaving(false);
    }
  };

  const sendInvite = async (guardian) => {
    setBusyId(guardian.id);
    try {
      const r = await clinicAPI.sendAthleteGuardianInvite(patientId, guardian.id);
      if (r.email_sent) {
        toast.success("Приглашение отправлено на email");
      } else if (r.invite_url) {
        await navigator.clipboard.writeText(r.invite_url);
        toast.success("Ссылка скопирована — отправьте родителю");
      } else {
        toast.success("Приглашение создано");
      }
      load();
      onChanged?.();
    } catch (err) {
      toast.error(err.response?.data?.error || "Не удалось отправить");
    } finally {
      setBusyId(null);
    }
  };

  const removeGuardian = async (guardian) => {
    if (!window.confirm(`Убрать ${guardian.contact_name} из ответственных?`)) return;
    setBusyId(guardian.id);
    try {
      await clinicAPI.removeAthleteGuardian(patientId, guardian.id);
      toast.success("Связь удалена");
      load();
      onChanged?.();
    } catch (err) {
      toast.error(err.response?.data?.error || "Ошибка");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <section className={s.clientTabCard}>
      <div className={s.summaryBlockHead}>
        <h3 className={s.clientTabCardTitle}>Родители / ответственные</h3>
        {!showForm ? (
          <Button type="button" size="sm" onClick={() => setShowForm(true)}>
            + Добавить
          </Button>
        ) : null}
      </div>

      <p className={s.onboardingSheetHint}>
        Родитель получает доступ к порталу и видит расписание, абонемент и новости
        выбранного спортсмена. Телефон — ключ для автоматической привязки при регистрации.
      </p>

      {loading ? (
        <p className={s.summaryEmptyNote}>Загрузка…</p>
      ) : guardians.length === 0 && !showForm ? (
        <p className={s.summaryEmptyNote}>Родители не указаны</p>
      ) : (
        <ul className={s.guardianList}>
          {guardians.map((g) => (
            <li key={g.id} className={s.guardianRow}>
              <div className={s.guardianRowMain}>
                <strong>{g.contact_name}</strong>
                <span className={s.guardianStatus}>
                  {STATUS_LABELS[g.invite_status] || g.invite_status}
                </span>
                {g.relation ? (
                  <span className={s.guardianMeta}>{g.relation}</span>
                ) : null}
                {g.contact_phone ? (
                  <a href={`tel:${g.contact_phone}`} className={s.guardianMeta}>
                    {g.contact_phone}
                  </a>
                ) : null}
                {g.contact_email ? (
                  <span className={s.guardianMeta}>{g.contact_email}</span>
                ) : null}
              </div>
              <div className={s.guardianRowActions}>
                {g.invite_status !== "registered" ? (
                  <Button
                    type="button"
                    size="sm"
                    variant="secondary"
                    disabled={busyId === g.id}
                    onClick={() => sendInvite(g)}
                  >
                    {busyId === g.id ? "…" : "Пригласить"}
                  </Button>
                ) : null}
                <button
                  type="button"
                  className={s.guardianRemoveBtn}
                  disabled={busyId === g.id}
                  onClick={() => removeGuardian(g)}
                >
                  Убрать
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}

      {showForm ? (
        <form className={s.clientTabFormStack} onSubmit={addGuardian}>
          <Field label="ФИО родителя *">
            <Input
              value={form.contact_name}
              onChange={(e) => setForm({ ...form, contact_name: e.target.value })}
              autoFocus
            />
          </Field>
          <Field label="Телефон *">
            <Input
              type="tel"
              value={form.contact_phone}
              onChange={(e) => setForm({ ...form, contact_phone: e.target.value })}
              placeholder="+7 …"
            />
          </Field>
          <Field label="Email">
            <Input
              type="email"
              value={form.contact_email}
              onChange={(e) => setForm({ ...form, contact_email: e.target.value })}
            />
          </Field>
          <Field label="Кем приходится">
            <Input
              value={form.relation}
              onChange={(e) => setForm({ ...form, relation: e.target.value })}
              placeholder="Мама, папа…"
            />
          </Field>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <Button type="submit" size="sm" disabled={saving}>
              {saving ? "Сохранение…" : "Сохранить"}
            </Button>
            <Button
              type="button"
              size="sm"
              variant="secondary"
              onClick={() => {
                setShowForm(false);
                setForm(emptyForm());
              }}
            >
              Отмена
            </Button>
          </div>
        </form>
      ) : null}
    </section>
  );
}
